import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock, Users, Shuffle, BarChart3, Youtube, Gamepad2 } from 'lucide-react';

import { API, BACKEND_URL } from './admin/adminUtils';
import PlayersTab from './admin/PlayersTab';
import TeamsDivisionTab from './admin/TeamsDivisionTab';
import StatsTab from './admin/StatsTab';
import MatchesTab from './admin/MatchesTab';
import ReplaysTab from './admin/ReplaysTab';
import LiveGameTab from './admin/LiveGameTab';

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('jogadores');
  const [players, setPlayers] = useState([]);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [teams, setTeams] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [editingName, setEditingName] = useState(null);
  const [newName, setNewName] = useState('');
  const [editingPassword, setEditingPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [matches, setMatches] = useState([]);
  const [newMatch, setNewMatch] = useState({
    date: '', time: '', location: '', location_url: '',
    total_spots: 14, field_cost: 50, player_price: 2.75, is_open: false,
  });
  const [editingMatch, setEditingMatch] = useState(null);
  const [voteHistory, setVoteHistory] = useState([]);
  const [moments, setMoments] = useState([]);
  const [uploadingMoment, setUploadingMoment] = useState(false);
  const [momentCaption, setMomentCaption] = useState('');
  const [paymentsSummary, setPaymentsSummary] = useState([]);
  const [selectedMatchPayments, setSelectedMatchPayments] = useState(null);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [caixaHistory, setCaixaHistory] = useState(null);
  const [newTransaction, setNewTransaction] = useState({ type: 'saida', amount: '', description: '', date: '' });
  const [editingPhone, setEditingPhone] = useState(null);
  const [newPhone, setNewPhone] = useState('');
  const [showImportPhones, setShowImportPhones] = useState(false);
  const [importCsvData, setImportCsvData] = useState('');
  const [importingPhones, setImportingPhones] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [editingHighlightPhoto, setEditingHighlightPhoto] = useState(null);
  const [uploadingHighlightPhoto, setUploadingHighlightPhoto] = useState(null);
  const [teamPhotoUrl, setTeamPhotoUrl] = useState('');
  const [uploadingTeamPhoto, setUploadingTeamPhoto] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPlayers();
      fetchMatches();
      fetchSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (activeTab === 'historico') fetchVoteHistory();
    if (activeTab === 'pagamentos') fetchPaymentsSummary();
    if (activeTab === 'caixa') fetchCaixaHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeTab]);

  // ========== FETCH FUNCTIONS ==========
  const fetchPlayers = async () => {
    try {
      const response = await axios.get(`${API}/players`);
      setPlayers(response.data);
    } catch (error) {
      console.error('Erro ao carregar jogadores:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setTeamPhotoUrl(response.data.team_photo_url || '');
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/admin/matches`);
      setMatches(response.data);
    } catch (error) {
      console.error('Erro ao carregar partidas:', error);
    }
  };

  const fetchVoteHistory = async () => {
    try {
      const response = await axios.get(`${API}/admin/vote-history`);
      setVoteHistory(response.data.votes);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const fetchMoments = async () => {
    try {
      const response = await axios.get(`${API}/moments`);
      setMoments(response.data.moments);
    } catch (error) {
      console.error('Erro ao carregar momentos:', error);
    }
  };

  const fetchPaymentsSummary = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-payments`);
      setPaymentsSummary(response.data.matches);
    } catch (error) {
      console.error('Erro ao carregar pagamentos:', error);
    }
  };

  const fetchMatchPayments = async (matchId) => {
    setLoadingPayments(true);
    try {
      const response = await axios.get(`${API}/admin/match-payments/${matchId}`);
      setSelectedMatchPayments(response.data);
    } catch (error) {
      console.error('Erro ao carregar pagamentos da partida:', error);
      alert('Erro ao carregar pagamentos');
    }
    setLoadingPayments(false);
  };

  const fetchCaixaHistory = async () => {
    try {
      const response = await axios.get(`${API}/admin/caixa-history`);
      setCaixaHistory(response.data);
    } catch (error) {
      console.error('Erro ao carregar histórico do caixa:', error);
    }
  };

  // ========== HANDLERS ==========
  const handleUploadTeamPhoto = async (file) => {
    if (!file) return;
    setUploadingTeamPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadResponse = await axios.post(`${API}/admin/upload-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fullUrl = `${BACKEND_URL}${uploadResponse.data.url}`;
      await axios.put(`${API}/admin/settings/team-photo`, { team_photo_url: fullUrl });
      setTeamPhotoUrl(fullUrl);
      alert('Foto do time atualizada com sucesso!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao fazer upload da foto');
    }
    setUploadingTeamPhoto(false);
  };

  const handleUploadMoment = async (file, caption) => {
    setUploadingMoment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadResponse = await axios.post(`${API}/admin/upload-moment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await axios.post(`${API}/admin/moments`, {
        file_url: uploadResponse.data.url,
        file_type: uploadResponse.data.file_type,
        caption: caption,
      });
      setUploadingMoment(false);
      setMomentCaption('');
      fetchMoments();
      alert('Momento adicionado com sucesso!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao fazer upload');
      setUploadingMoment(false);
    }
  };

  const handleDeleteMoment = async (momentId) => {
    if (!window.confirm('Tem certeza que deseja remover este momento?')) return;
    try {
      await axios.delete(`${API}/admin/moments/${momentId}`);
      fetchMoments();
    } catch (error) {
      alert('Erro ao remover momento');
    }
  };

  const handleUpdatePayment = async (paymentId, isPaid, notes = '') => {
    try {
      await axios.put(`${API}/admin/payment`, { payment_id: paymentId, is_paid: isPaid, notes });
      if (selectedMatchPayments) {
        fetchMatchPayments(selectedMatchPayments.match_id);
      }
      fetchPaymentsSummary();
    } catch (error) {
      alert('Erro ao atualizar pagamento');
    }
  };

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    if (!newTransaction.amount || !newTransaction.description) {
      alert('Preencha o valor e a descrição');
      return;
    }
    try {
      await axios.post(`${API}/admin/caixa-transaction`, {
        type: newTransaction.type,
        amount: parseFloat(newTransaction.amount),
        description: newTransaction.description,
        date: newTransaction.date || null,
      });
      setNewTransaction({ type: 'saida', amount: '', description: '', date: '' });
      fetchCaixaHistory();
      alert('Transação registada!');
    } catch (error) {
      alert('Erro ao registar transação');
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Tem certeza que deseja remover esta transação?')) return;
    try {
      await axios.delete(`${API}/admin/caixa-transaction/${transactionId}`);
      fetchCaixaHistory();
    } catch (error) {
      alert('Erro ao remover transação');
    }
  };

  const handleResetRankings = async () => {
    if (!window.confirm('⚠️ ATENÇÃO!\n\nIsto vai APAGAR todos os votos e resetar o ranking de TODOS os jogadores para zero.\n\nTem certeza que deseja continuar?')) return;
    if (!window.confirm('Esta ação NÃO pode ser desfeita. Confirma o reset dos rankings?')) return;
    try {
      await axios.post(`${API}/admin/reset-rankings`);
      fetchPlayers();
      alert('Rankings resetados com sucesso! Todos os jogadores começam do zero.');
    } catch (error) {
      alert('Erro ao resetar rankings');
    }
  };

  const handleResetMonthlyStats = async () => {
    if (!window.confirm('🏆 FECHAR O MÊS\n\nIsto vai:\n• Atribuir os títulos do mês (Artilheiro, Garçom, MVP, Relógio de Ouro)\n• Acumular as estatísticas do mês na Central de Estatísticas\n• Zerar Gols, Assistências, Faltas, Jogos, Chutes Fora\n\nO ranking (estrelas) NÃO é afetado.\n\nConfirmas?')) return;
    if (!window.confirm('Esta ação não pode ser desfeita. Tens a certeza?')) return;
    try {
      const res = await axios.post(`${API}/admin/reset-monthly-stats`);
      fetchPlayers();
      const period = res.data?.history?.period_label || 'mês';
      alert(`✅ Mês fechado: ${period}.\nTítulos atribuídos e estatísticas mensais zeradas.`);
    } catch (error) {
      alert('Erro ao fechar o mês: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsAuthenticated(true);
    } catch (error) {
      alert('Senha incorreta');
    }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    if (!newPlayerName.trim()) return;
    try {
      await axios.post(`${API}/players`, { name: newPlayerName });
      setNewPlayerName('');
      fetchPlayers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao adicionar jogador');
    }
  };

  const handleCheckIn = async (playerName, isPresent) => {
    try {
      await axios.put(`${API}/admin/check-in`, { player_name: playerName, is_present: isPresent });
      fetchPlayers();
    } catch (error) {
      console.error('Erro ao atualizar check-in:', error);
    }
  };

  const handleUncheckAll = async () => {
    if (!window.confirm('Desmarcar TODOS os jogadores do check-in?')) return;
    try {
      const checkedPlayers = players.filter(p => p.is_present);
      for (const player of checkedPlayers) {
        await axios.put(`${API}/admin/check-in`, { player_name: player.name, is_present: false });
      }
      fetchPlayers();
      alert(`✅ ${checkedPlayers.length} jogador(es) desmarcado(s)`);
    } catch (error) {
      console.error('Erro ao desmarcar jogadores:', error);
      alert('Erro ao desmarcar jogadores');
    }
  };

  const handleToggleTitular = async (playerName, isTitular) => {
    try {
      await axios.put(`${API}/admin/player-titular`, { player_name: playerName, is_titular: isTitular });
      fetchPlayers();
    } catch (error) {
      alert('Erro ao atualizar status de titular');
    }
  };

  const handleDrawTeams = async (numTeams = 3) => {
    try {
      const response = await axios.post(`${API}/admin/draw-teams?num_teams=${numTeams}`);
      setTeams(response.data);
      setActiveTab('sorteio');
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao sortear times');
    }
  };

  const handleCopyTeams = () => {
    if (teams) {
      navigator.clipboard.writeText(teams.whatsapp_text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleUpdateStats = async (playerName, field, value) => {
    const player = players.find(p => p.name === playerName);
    if (!player) return;
    const FIELDS = [
      'goals', 'assists', 'shots_missed', 'shots_on_target',
      'passes_completed', 'passes_failed', 'tackles', 'blocks',
      'clean_sheets', 'saves', 'own_goals', 'wins', 'draws',
      'fouls_committed', 'fouls_suffered', 'games_played',
      'dribbles', 'interceptions', 'goals_conceded',
      'possession_lost', 'control_errors', 'throws',
    ];
    const payload = { player_name: playerName };
    FIELDS.forEach(f => {
      payload[f] = (f === field ? value : (player[f] || 0));
    });
    try {
      await axios.put(`${API}/admin/player-stats`, payload);
      fetchPlayers();
    } catch (error) {
      console.error('Erro ao atualizar estatísticas:', error);
    }
  };

  const handleUpdatePlayerPosition = async (playerName, position) => {
    try {
      await axios.put(`${API}/admin/player-position`, { player_name: playerName, position });
      fetchPlayers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao atualizar posição');
    }
  };

  const handleOpenSessionAwards = async (matchId) => {
    if (!window.confirm('Abrir votação dos prémios da rodada (Puskás, Pintura, Melhor Defesa)?\n\nApenas jogadores confirmados na partida poderão votar.')) return;
    try {
      await axios.post(`${API}/admin/sessions/${matchId}/open-awards`);
      fetchMatches();
      alert('🏆 Votação aberta! Os jogadores podem votar em /votar');
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao abrir votação');
    }
  };

  const handleCloseSessionAwards = async (matchId) => {
    if (!window.confirm('Encerrar a votação dos prémios?\n\nIsto vai apurar os vencedores e atribuir os títulos. Esta ação não pode ser desfeita.')) return;
    try {
      const res = await axios.post(`${API}/admin/sessions/${matchId}/close-awards`);
      const winners = res.data?.winners || {};
      let msg = '🏆 Vencedores apurados!\n';
      const labels = { puskas: '🥇 Puskás', pintura: '🎨 Pintura', melhor_defesa: '🧤 Melhor Defesa' };
      Object.entries(winners).forEach(([cat, w]) => {
        msg += `${labels[cat] || cat}: ${w.name} (${w.votes} votos)\n`;
      });
      if (Object.keys(winners).length === 0) msg += 'Nenhum voto foi registado.';
      fetchMatches();
      alert(msg);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao encerrar votação');
    }
  };

  const handleDeletePlayer = async (playerName) => {
    if (!window.confirm(`Tem certeza que deseja remover ${playerName}?`)) return;
    try {
      await axios.delete(`${API}/admin/players/${playerName}`);
      fetchPlayers();
    } catch (error) {
      alert('Erro ao remover jogador');
    }
  };

  const handleUpdatePlayerName = async (oldName) => {
    if (!newName.trim()) {
      alert('Por favor, digite o novo nome');
      return;
    }
    if (newName.trim() === oldName) {
      setEditingName(null);
      setNewName('');
      return;
    }
    try {
      await axios.put(`${API}/admin/player-name`, { old_name: oldName, new_name: newName.trim() });
      setEditingName(null);
      setNewName('');
      fetchPlayers();
      fetchMatches();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao atualizar nome');
    }
  };

  const handleUpdatePlayerPassword = async (playerName) => {
    if (!newPassword.trim()) {
      alert('Por favor, digite a senha');
      return;
    }
    try {
      await axios.put(`${API}/admin/player-password`, { player_name: playerName, password: newPassword.trim() });
      setEditingPassword(null);
      setNewPassword('');
      alert('Senha atualizada com sucesso!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao atualizar senha');
    }
  };

  const handleUpdatePlayerPhone = async (playerName) => {
    try {
      await axios.put(`${API}/admin/player-phone`, { player_name: playerName, phone: newPhone.trim() });
      setEditingPhone(null);
      setNewPhone('');
      fetchPlayers();
      alert('Telefone atualizado com sucesso!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao atualizar telefone');
    }
  };

  const handleImportPhones = async () => {
    if (!importCsvData.trim()) {
      alert('Cole os dados no formato: nome,telefone (um por linha)');
      return;
    }
    setImportingPhones(true);
    setImportResult(null);
    try {
      const response = await axios.post(`${API}/admin/import-phones`, { csv_data: importCsvData });
      setImportResult(response.data);
      fetchPlayers();
      if (response.data.updated_count > 0) {
        alert(`✅ ${response.data.updated_count} telefone(s) importado(s) com sucesso!`);
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao importar telefones');
    }
    setImportingPhones(false);
  };

  const handleUpdatePhoto = async (playerName, file) => {
    if (!file) {
      alert('Por favor, selecione uma imagem');
      return;
    }
    setUploadingPhoto(playerName);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadResponse = await axios.post(`${API}/admin/upload-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fullUrl = `${BACKEND_URL}${uploadResponse.data.url}`;
      await axios.put(`${API}/admin/profile-image`, { player_name: playerName, profile_image_url: fullUrl });
      setEditingPhoto(null);
      setUploadingPhoto(null);
      fetchPlayers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao fazer upload da foto');
      setUploadingPhoto(null);
    }
  };

  const handleUpdateHighlightPhoto = async (playerName, file) => {
    if (!file) {
      alert('Por favor, selecione uma imagem');
      return;
    }
    setUploadingHighlightPhoto(playerName);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadResponse = await axios.post(`${API}/admin/upload-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fullUrl = `${BACKEND_URL}${uploadResponse.data.url}`;
      await axios.put(`${API}/admin/player-highlight-image`, { player_name: playerName, highlight_image_url: fullUrl });
      setEditingHighlightPhoto(null);
      setUploadingHighlightPhoto(null);
      fetchPlayers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao fazer upload da foto de destaque');
      setUploadingHighlightPhoto(null);
    }
  };

  const [editingCardPhoto, setEditingCardPhoto] = useState(null);
  const [uploadingCardPhoto, setUploadingCardPhoto] = useState(null);
  const handleUpdateCardPhoto = async (playerName, file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      alert('Imagem demasiado grande. Máx. 3 MB.');
      return;
    }
    setUploadingCardPhoto(playerName);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await axios.post(`${API}/admin/upload-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fullUrl = `${BACKEND_URL}${r.data.url}`;
      await axios.put(`${API}/admin/player-card-image`, { player_name: playerName, card_image_url: fullUrl });
      setEditingCardPhoto(null);
      fetchPlayers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao enviar a foto da carta');
    }
    setUploadingCardPhoto(null);
  };
  const handleUpdateNationality = async (playerName, nationality) => {
    try {
      await axios.put(`${API}/admin/player-nationality`, { player_name: playerName, nationality });
      fetchPlayers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao atualizar nacionalidade');
    }
  };
  const handleRemoveCardPhoto = async (playerName) => {
    if (!window.confirm('Remover a foto da carta?')) return;
    try {
      await axios.put(`${API}/admin/player-card-image`, { player_name: playerName, card_image_url: '' });
      fetchPlayers();
    } catch (err) {
      alert('Erro ao remover');
    }
  };

  const handleCreateMatch = async (e) => {
    e.preventDefault();
    if (!newMatch.date || !newMatch.time || !newMatch.location) {
      alert('Por favor, preencha todos os campos');
      return;
    }
    try {
      await axios.post(`${API}/admin/matches`, newMatch);
      setNewMatch({ date: '', time: '', location: '', location_url: '', total_spots: 14, field_cost: 50, player_price: 2.75, is_open: false });
      fetchMatches();
      alert('Partida criada com sucesso! Lembre-se de LIBERAR quando quiser abrir as vagas.');
    } catch (error) {
      alert('Erro ao criar partida');
    }
  };

  const handleUpdateMatch = async (matchId) => {
    if (!editingMatch) return;
    try {
      await axios.put(`${API}/admin/matches`, {
        match_id: matchId,
        date: editingMatch.date,
        time: editingMatch.time,
        location: editingMatch.location,
        total_spots: editingMatch.total_spots,
        field_cost: editingMatch.field_cost,
        player_price: editingMatch.player_price,
        is_open: editingMatch.is_open,
      });
      setEditingMatch(null);
      fetchMatches();
      alert('Partida atualizada!');
    } catch (error) {
      alert('Erro ao atualizar partida');
    }
  };

  const handleToggleMatchOpen = async (matchId, isOpen) => {
    try {
      await axios.put(`${API}/admin/matches`, { match_id: matchId, is_open: isOpen });
      fetchMatches();
    } catch (error) {
      alert('Erro ao atualizar partida');
    }
  };

  const handleTogglePlayerConfirmation = async (matchId, playerName, confirmed) => {
    const match = matches.find(m => m.id === matchId);
    if (!match) return;
    let updatedPlayers;
    if (confirmed) {
      updatedPlayers = [...match.confirmed_players, playerName];
    } else {
      updatedPlayers = match.confirmed_players.filter(p => p !== playerName);
    }
    try {
      await axios.put(`${API}/admin/matches`, { match_id: matchId, confirmed_players: updatedPlayers });
      fetchMatches();
    } catch (error) {
      alert('Erro ao atualizar confirmações');
    }
  };

  const handleDeleteMatch = async (matchId) => {
    if (!window.confirm('Tem certeza que deseja remover esta partida?')) return;
    try {
      await axios.delete(`${API}/admin/matches/${matchId}`);
      fetchMatches();
    } catch (error) {
      alert('Erro ao remover partida');
    }
  };

  const handleArchiveMatch = async (matchId) => {
    if (!window.confirm('📦 ARQUIVAR PARTIDA\n\nIsto vai:\n• Calcular o resultado financeiro líquido (recebido − custo do campo)\n• Criar uma transação permanente no Caixa com este valor\n• Remover a partida da lista (o dinheiro fica preservado no caixa)\n\nUsa isto para partidas que já aconteceram. Confirmas?')) return;
    try {
      const res = await axios.post(`${API}/admin/matches/${matchId}/archive`);
      const net = res.data?.net ?? 0;
      fetchMatches();
      if (selectedMatchPayments?.match_id === matchId) {
        setSelectedMatchPayments(null);
      }
      alert(`✅ Partida arquivada!\nResultado líquido: ${net >= 0 ? '+' : ''}${net.toFixed(2)}€\n(Disponível na aba Caixa)`);
    } catch (error) {
      alert('Erro ao arquivar partida: ' + (error.response?.data?.detail || error.message));
    }
  };

  // ========== AUTH GATE ==========
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="card max-w-md w-full">
          <div className="text-center mb-6">
            <Lock size={64} strokeWidth={3} className="mx-auto mb-4" />
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">PAINEL ADMIN</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-xl font-bold mb-3 uppercase tracking-wider">Senha</label>
              <input
                type="password"
                className="input-field"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite a senha"
                data-testid="admin-password-input"
              />
            </div>
            <button type="submit" className="btn-primary w-full" data-testid="admin-login-btn">
              ENTRAR
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ========== CTX FOR TABS ==========
  const ctx = {
    players, newPlayerName, setNewPlayerName,
    teams, copied,
    matches, newMatch, setNewMatch, editingMatch, setEditingMatch,
    voteHistory, moments, momentCaption, setMomentCaption, uploadingMoment,
    paymentsSummary, selectedMatchPayments, setSelectedMatchPayments, loadingPayments,
    caixaHistory, newTransaction, setNewTransaction,
    editingPhoto, setEditingPhoto, uploadingPhoto,
    editingName, setEditingName, newName, setNewName,
    editingPassword, setEditingPassword, newPassword, setNewPassword,
    editingPhone, setEditingPhone, newPhone, setNewPhone,
    showImportPhones, setShowImportPhones, importCsvData, setImportCsvData,
    importingPhones, importResult, setImportResult,
    editingHighlightPhoto, setEditingHighlightPhoto, uploadingHighlightPhoto,
    teamPhotoUrl, setTeamPhotoUrl, uploadingTeamPhoto,
    // handlers
    handleUploadTeamPhoto, handleUploadMoment, handleDeleteMoment,
    handleUpdatePayment, fetchMatchPayments,
    handleCreateTransaction, handleDeleteTransaction,
    handleResetRankings, handleResetMonthlyStats,
    handleAddPlayer, handleCheckIn, handleUncheckAll, handleToggleTitular,
    handleDrawTeams, handleCopyTeams, handleUpdateStats, handleUpdatePlayerPosition, handleDeletePlayer,
    handleUpdatePlayerName, handleUpdatePlayerPassword, handleUpdatePlayerPhone,
    handleImportPhones, handleUpdatePhoto, handleUpdateHighlightPhoto,
    editingCardPhoto, setEditingCardPhoto, uploadingCardPhoto,
    handleUpdateCardPhoto, handleRemoveCardPhoto, handleUpdateNationality,
    handleCreateMatch, handleUpdateMatch, handleToggleMatchOpen,
    handleTogglePlayerConfirmation, handleDeleteMatch, handleArchiveMatch,
    handleOpenSessionAwards, handleCloseSessionAwards,
  };

  // ========== MAIN VIEW ==========
  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Navigation */}
        <div className="flex flex-wrap gap-3 justify-between items-center mb-8">
          <a href="/" className="btn-secondary text-sm sm:text-base py-3 px-6" data-testid="go-to-home">
            ⚽ VOTAÇÃO
          </a>
          <div className="flex gap-3">
            <a href="/rankings" className="btn-secondary text-sm sm:text-base py-3 px-6" data-testid="go-to-rankings">
              🏆 RANKINGS
            </a>
            <a href="/partidas" className="btn-secondary text-sm sm:text-base py-3 px-6" data-testid="go-to-matches">
              📅 PARTIDAS
            </a>
          </div>
        </div>

        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-6 text-center">
          ⚙️ PAINEL ADMINISTRATIVO
        </h1>

        {/* Quick Actions bar — atalhos unificados */}
        <div className="mb-8 bg-gradient-to-r from-zinc-900 to-zinc-800 text-white rounded-2xl p-4 border-2 border-zinc-700" data-testid="admin-quick-actions">
          <p className="text-[10px] font-black uppercase tracking-widest text-yellow-400 mb-2">⚡ ATALHOS RÁPIDOS</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <button
              onClick={() => setActiveTab('partidas')}
              className="bg-blue-600 hover:bg-blue-500 rounded-lg px-3 py-3 text-left transition-colors"
              data-testid="qa-nova-partida"
            >
              <p className="text-xs opacity-80">➕ Nova</p>
              <p className="text-sm font-black uppercase leading-tight">Criar Partida</p>
            </button>
            <button
              onClick={() => setActiveTab('partidas')}
              className="bg-purple-600 hover:bg-purple-500 rounded-lg px-3 py-3 text-left transition-colors"
              data-testid="qa-votacao"
            >
              <p className="text-xs opacity-80">🗳️ Prémios</p>
              <p className="text-sm font-black uppercase leading-tight">Controlar Votação</p>
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className="bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg px-3 py-3 text-left transition-colors"
              data-testid="qa-atribuir-premios"
            >
              <p className="text-xs opacity-80">🏆 Fim de Mês</p>
              <p className="text-sm font-black uppercase leading-tight">Atribuir Prémios</p>
            </button>
            <button
              onClick={() => setActiveTab('edits')}
              className="bg-green-600 hover:bg-green-500 rounded-lg px-3 py-3 text-left transition-colors"
              data-testid="qa-jogo-ao-vivo"
            >
              <p className="text-xs opacity-80">🎮 Live</p>
              <p className="text-sm font-black uppercase leading-tight">Jogo Ao Vivo</p>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-4 mb-8 justify-center">
          <button
            onClick={() => setActiveTab('jogadores')}
            className={activeTab === 'jogadores' ? 'btn-primary' : 'btn-secondary'}
            data-testid="tab-jogadores"
          >
            <Users className="inline mr-2" size={24} strokeWidth={3} />
            JOGADORES
          </button>
          <button
            onClick={() => setActiveTab('sorteio')}
            className={activeTab === 'sorteio' ? 'btn-primary' : 'btn-secondary'}
            data-testid="tab-sorteio"
          >
            <Shuffle className="inline mr-2" size={24} strokeWidth={3} />
            DIVISÃO DE TIMES
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={activeTab === 'stats' ? 'btn-primary' : 'btn-secondary'}
            data-testid="tab-stats"
          >
            <BarChart3 className="inline mr-2" size={24} strokeWidth={3} />
            ESTATÍSTICAS
          </button>
          <button
            onClick={() => setActiveTab('partidas')}
            className={activeTab === 'partidas' ? 'btn-primary' : 'btn-secondary'}
            data-testid="tab-partidas"
          >
            📅 PARTIDAS
          </button>
          <button
            onClick={() => setActiveTab('replays')}
            className={activeTab === 'replays' ? 'btn-primary' : 'btn-secondary'}
            data-testid="tab-replays"
          >
            <Youtube className="inline mr-2" size={24} strokeWidth={3} />
            REPLAYS
          </button>
          <button
            onClick={() => setActiveTab('edits')}
            className={activeTab === 'edits' ? 'btn-primary' : 'btn-secondary'}
            data-testid="tab-live"
          >
            <Gamepad2 className="inline mr-2" size={24} strokeWidth={3} />
            JOGO AO VIVO
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'jogadores' && <PlayersTab ctx={ctx} />}
        {activeTab === 'sorteio' && <TeamsDivisionTab ctx={ctx} />}
        {activeTab === 'stats' && <StatsTab ctx={ctx} />}
        {activeTab === 'partidas' && <MatchesTab ctx={ctx} />}
        {activeTab === 'replays' && <ReplaysTab />}
        {activeTab === 'edits' && <LiveGameTab ctx={ctx} />}
      </div>
    </div>
  );
};

export default AdminPage;
